import { useState } from "react";
import { ArrowUpRight, MapPin, Phone, Mail, MessageCircle, Crosshair } from "lucide-react";
import { FadeUp, Overline } from "@/components/Motion";
import { SITE, BUYER_TYPES, PRODUCTS, buildWhatsAppLink } from "@/data/site";
import { useLang, TA, u } from "@/data/i18n";

const initial = { buyerType: "", name: "", business: "", whatsapp: "", location: "", product: "", message: "" };

export default function Enquiry() {
  const { lang } = useLang();
  const [form, setForm] = useState(initial);
  const [locating, setLocating] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const detectLocation = () => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm((f) => ({ ...f, location: `${pos.coords.latitude.toFixed(5)}, ${pos.coords.longitude.toFixed(5)}` }));
        setLocating(false);
      },
      () => setLocating(false),
      { timeout: 8000 }
    );
  };

  const submit = (e) => {
    e.preventDefault();
    const lines = [
      "New Enquiry — lovelyfoodpacks.com",
      `Buyer Type: ${form.buyerType}`,
      `Name: ${form.name}`,
      form.business && `Business: ${form.business}`,
      `WhatsApp: ${form.whatsapp}`,
      form.location && `Location: ${form.location}`,
      form.product && `Product Interest: ${form.product}`,
      form.message && `Message: ${form.message}`,
    ].filter(Boolean);
    window.open(buildWhatsAppLink(lines.join("\n")), "_blank");
  };

  const mailto = () => {
    const body = [
      `Buyer Type: ${form.buyerType}`, `Name: ${form.name}`, `Business: ${form.business}`,
      `WhatsApp: ${form.whatsapp}`, `Location: ${form.location}`, `Product: ${form.product}`, `Message: ${form.message}`,
    ].join("\n");
    window.location.href = `mailto:${SITE.email}?subject=${encodeURIComponent("Website Enquiry — " + (form.business || form.name))}&body=${encodeURIComponent(body)}`;
  };

  const inputCls =
    "w-full bg-white border border-ink/15 px-4 py-3.5 text-sm md:text-base text-ink placeholder:text-muted/60 outline-none transition-colors duration-300 focus:border-leaf";

  return (
    <section id="contact" data-testid="enquiry-section" className="py-24 md:py-32 bg-paper">
      <div className="max-w-[1500px] mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-10">
        {/* Contact info */}
        <div className="lg:col-span-5">
          <FadeUp>
            <Overline>{u(lang, "contact_overline")}</Overline>
            <h2 className="mt-4 font-display font-black tracking-tight text-4xl md:text-5xl text-leaf leading-[1.02]">
              {u(lang, "contact_heading")}
            </h2>
            <p className="mt-5 text-muted leading-relaxed text-base md:text-lg max-w-md">
              {u(lang, "contact_intro")}
            </p>
          </FadeUp>

          <div className="mt-10 flex flex-col gap-5">
            <FadeUp delay={0.05}>
              <a
                data-testid="contact-whatsapp-sales"
                href={buildWhatsAppLink("Hello Lovely Foodpacks! I'd like to chat with sales.")}
                target="_blank"
                rel="noreferrer"
                className="group flex items-center gap-4 border border-ink/10 bg-white p-5 transition-all duration-300 hover:border-leaf hover:shadow-2xl"
              >
                <MessageCircle size={24} className="text-leaf-bright" />
                <div>
                  <p className="text-[11px] tracking-[0.2em] uppercase text-muted font-bold">{u(lang, "wa_sales")}</p>
                  <p className="font-display font-bold text-lg text-leaf">{SITE.whatsappDisplay}</p>
                </div>
                <ArrowUpRight size={18} className="ml-auto text-muted group-hover:text-leaf transition-colors" />
              </a>
            </FadeUp>
            <FadeUp delay={0.1}>
              <a data-testid="contact-office-phone" href="tel:+914562275519" className="flex items-center gap-4 border border-ink/10 bg-white p-5 transition-all duration-300 hover:border-leaf">
                <Phone size={24} className="text-leaf-bright" />
                <div>
                  <p className="text-[11px] tracking-[0.2em] uppercase text-muted font-bold">{u(lang, "office")}</p>
                  <p className="font-display font-bold text-lg text-leaf">{SITE.officeDisplay}</p>
                </div>
              </a>
            </FadeUp>
            <FadeUp delay={0.15}>
              <a data-testid="contact-director-phone" href="tel:+919442223442" className="flex items-center gap-4 border border-ink/10 bg-white p-5 transition-all duration-300 hover:border-leaf">
                <Phone size={24} className="text-leaf-bright" />
                <div>
                  <p className="text-[11px] tracking-[0.2em] uppercase text-muted font-bold">{u(lang, "director")} — {SITE.md} ({SITE.mdDegrees})</p>
                  <p className="font-display font-bold text-lg text-leaf">{SITE.directorDisplay}</p>
                </div>
              </a>
            </FadeUp>
            <FadeUp delay={0.2}>
              <a data-testid="contact-email" href={`mailto:${SITE.email}`} className="flex items-center gap-4 border border-ink/10 bg-white p-5 transition-all duration-300 hover:border-leaf">
                <Mail size={24} className="text-leaf-bright" />
                <div>
                  <p className="text-[11px] tracking-[0.2em] uppercase text-muted font-bold">{u(lang, "email")}</p>
                  <p className="font-display font-bold text-lg text-leaf break-all">{SITE.email}</p>
                </div>
              </a>
            </FadeUp>
          </div>

          <FadeUp delay={0.25}>
            <div className="mt-8 border border-ink/10 bg-white">
              <iframe
                data-testid="head-office-map"
                title="Lovely Foodpacks Head Office — Sivakasi"
                src={SITE.mapEmbed}
                className="w-full h-64 md:h-72"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
              <div className="p-5 flex gap-3">
                <MapPin size={20} className="text-leaf-bright shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-bold text-leaf">{u(lang, "head_office")}</p>
                  <p className="text-sm text-muted leading-relaxed">{SITE.hoAddress}</p>
                  <p className="mt-2 text-sm font-bold text-leaf">{u(lang, "factory")}</p>
                  <p className="text-sm text-muted">{SITE.factoryAddress}</p>
                </div>
              </div>
            </div>
          </FadeUp>
        </div>

        {/* Enquiry form */}
        <div className="lg:col-span-7">
          <FadeUp delay={0.1}>
            <form
              data-testid="enquiry-form"
              onSubmit={submit}
              className="bg-leaf text-white noise-overlay relative p-7 md:p-12"
            >
              <Overline dark>{u(lang, "form_overline")}</Overline>
              <h3 className="mt-3 font-display font-black tracking-tight text-3xl md:text-4xl">
                {u(lang, "form_heading")}
              </h3>
              <p className="mt-3 text-white/60 text-sm md:text-base">
                {u(lang, "form_intro")}
              </p>

              <div className="mt-9 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] tracking-[0.2em] uppercase font-bold text-sun mb-2">{u(lang, "form_buyer")}</label>
                  <select data-testid="form-buyer-type" required value={form.buyerType} onChange={set("buyerType")} className={inputCls}>
                    <option value="" disabled>{u(lang, "form_buyer_ph")}</option>
                    {BUYER_TYPES.map((b, i) => <option key={b} value={b}>{lang === "ta" ? TA.buyerTypes[i] : b}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] tracking-[0.2em] uppercase font-bold text-sun mb-2">{u(lang, "form_name")}</label>
                  <input data-testid="form-name" required value={form.name} onChange={set("name")} placeholder={u(lang, "form_name_ph")} className={inputCls} />
                </div>
                <div>
                  <label className="block text-[11px] tracking-[0.2em] uppercase font-bold text-sun mb-2">{u(lang, "form_business")}</label>
                  <input data-testid="form-business" value={form.business} onChange={set("business")} placeholder={u(lang, "form_business_ph")} className={inputCls} />
                </div>
                <div>
                  <label className="block text-[11px] tracking-[0.2em] uppercase font-bold text-sun mb-2">{u(lang, "form_whatsapp")}</label>
                  <input data-testid="form-whatsapp" required value={form.whatsapp} onChange={set("whatsapp")} placeholder="+91 ..." className={inputCls} />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-[11px] tracking-[0.2em] uppercase font-bold text-sun mb-2">{u(lang, "form_location")}</label>
                  <div className="flex gap-2">
                    <input data-testid="form-location" value={form.location} onChange={set("location")} placeholder={u(lang, "form_location_ph")} className={inputCls} />
                    <button
                      type="button"
                      data-testid="form-detect-location"
                      onClick={detectLocation}
                      className="shrink-0 inline-flex items-center gap-2 border border-white/30 px-4 text-sm font-bold transition-colors duration-300 hover:border-sun hover:text-sun"
                    >
                      <Crosshair size={16} className={locating ? "animate-spin" : ""} />
                      {locating ? u(lang, "form_locating") : u(lang, "form_detect")}
                    </button>
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-[11px] tracking-[0.2em] uppercase font-bold text-sun mb-2">{u(lang, "form_product")}</label>
                  <select data-testid="form-product" value={form.product} onChange={set("product")} className={inputCls}>
                    <option value="">{u(lang, "form_product_ph")}</option>
                    {PRODUCTS.map((p) => <option key={p.id} value={p.name}>{lang === "ta" ? TA.products[p.id].name : p.name}</option>)}
                    <option value="Complete Catalogue">{u(lang, "full_catalogue")}</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-[11px] tracking-[0.2em] uppercase font-bold text-sun mb-2">{u(lang, "form_message")}</label>
                  <textarea data-testid="form-message" rows={3} value={form.message} onChange={set("message")} placeholder={u(lang, "form_message_ph")} className={`${inputCls} resize-none`} />
                </div>
              </div>

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <button
                  type="submit"
                  data-testid="form-submit-whatsapp"
                  className="group flex-1 inline-flex items-center justify-center gap-2 bg-sun text-leaf px-7 py-4 font-bold tracking-wide transition-colors duration-300 hover:bg-white"
                >
                  {u(lang, "form_send")}
                  <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </button>
                <button
                  type="button"
                  data-testid="form-submit-email"
                  onClick={mailto}
                  className="inline-flex items-center justify-center gap-2 border border-white/30 px-7 py-4 font-bold tracking-wide transition-colors duration-300 hover:border-sun hover:text-sun"
                >
                  {u(lang, "form_email")}
                </button>
              </div>
            </form>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}
